import * as React from 'react';
import urgentOrderIcon from 'assets/images/urgent_order.svg';
import { Typography } from 'components';
import styled, { FlattenSimpleInterpolation } from 'styled-components';

interface UrgentOrderAlertProps {
  children: React.ReactNode;
}

export const UrgentOrderText = ({
  children,
}: UrgentOrderAlertProps): React.ReactElement => {
  return (
    <Typography
      as="p"
      size="sm"
      color="gray_600"
      customStyle={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}
    >
      <img src={urgentOrderIcon} alt="Urgent Order" />
      <span style={{ marginLeft: '0.25rem' }}>{children}</span>
    </Typography>
  );
};

interface UrgentOrderAlertProps {
  children: React.ReactNode;
  containerStyle?: FlattenSimpleInterpolation;
}

export const UrgentOrderAlert = ({
  children,
  containerStyle,
}: UrgentOrderAlertProps): React.ReactElement => {
  return (
    <UrgentOrderWrapper containerStyle={containerStyle}>
      <Typography
        as="p"
        size="sm"
        color="gray_700"
        weight="semibold"
        customStyle={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}
      >
        <img
          src={urgentOrderIcon}
          alt="Urgent Order"
          style={{
            width: 20,
            height: 20,
            marginTop: 2,
          }}
        />
        <span style={{ marginLeft: '0.75rem', lineHeight: '1.4' }}>{children}</span>
      </Typography>
    </UrgentOrderWrapper>
  );
};

interface UrgentOrderBadgeProps {
  containerStyle?: FlattenSimpleInterpolation;
  tooltipText?: string;
  showTooltip?: boolean;
}

export const UrgentOrderBadge = ({
  containerStyle,
  tooltipText = 'An urgent fee will apply for pickups within 2 hours.',
  showTooltip = true,
}: UrgentOrderBadgeProps): React.ReactElement => {
  const [isTooltipVisible, setIsTooltipVisible] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent) => {
    if (showTooltip) {
      e.stopPropagation();
      setIsTooltipVisible(!isTooltipVisible);
    }
  };

  React.useEffect(() => {
    if (!showTooltip) {
      setIsTooltipVisible(false);
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsTooltipVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <UrgentOrderBadgeContainer
      ref={containerRef}
      onClick={handleClick}
      style={{ cursor: showTooltip ? 'pointer' : 'default' }}
    >
      <UrgentOrderBadgeWrapper containerStyle={containerStyle}>
        <img src={urgentOrderIcon} alt="Urgent Order" />
      </UrgentOrderBadgeWrapper>
      {showTooltip && isTooltipVisible && <Tooltip>{tooltipText}</Tooltip>}
    </UrgentOrderBadgeContainer>
  );
};

const UrgentOrderBadgeWrapper = styled.div<{
  containerStyle?: FlattenSimpleInterpolation;
}>`
  width: 20px;
  height: 20px;
  background-color: rgba(239, 104, 32, 0.15);
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  ${({ containerStyle }) => containerStyle}
`;

const UrgentOrderWrapper = styled.div<{ containerStyle?: FlattenSimpleInterpolation }>`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.gray_25};
  border-radius: 0.75rem;
  border: 1px solid ${({ theme }) => theme.colors.gray_300};
  gap: 0.75rem;
  ${({ containerStyle }) => containerStyle}
`;

const UrgentOrderBadgeContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const Tooltip = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  background-color: #fff;
  color: ${({ theme }) => theme.colors.gray_400};
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  width: 260px;
  z-index: 1000;
`;
